// Single source of truth for pillar names + colors.
//
// Adopts the palette already shipped in the Capacity donut + Leverage Lab tabs
// (purple Growth, blue Strategy, amber Ops, emerald Revenue, slate Non-Strategic).
//
// normalizePillar accepts both full dropdown labels ("Revenue & Sales") and the
// short legacy aliases ("Revenue") that trapDataCurrent / trapDataAfter / baseline
// rollups in page.tsx still emit.

export type PillarKey =
  | "Revenue & Sales"
  | "Growth & Brand"
  | "Operations & Delivery"
  | "Strategy & Culture"
  | "Non Strategic"
  | "Administrative & Internal"; // legacy: present on pre-redesign sessions

export interface PillarMeta {
  key: PillarKey | string;
  label: string; // full display name
  short: string; // mobile tab abbreviation — kept tight so 5 tabs fit ~375px viewport
  aliases?: string[]; // legacy short names accepted by normalizePillar for back-compat
  color: string; // canonical hex
  soft: string; // matching translucent fill for tints / backgrounds
}

// Indexed by the full dropdown label.
export const PILLARS: Record<string, PillarMeta> = {
  "Revenue & Sales": {
    key: "Revenue & Sales",
    label: "Revenue & Sales",
    short: "Rev",
    aliases: ["Revenue"],
    color: "#10b981",
    soft: "rgba(16,185,129,0.1)",
  },
  "Growth & Brand": {
    key: "Growth & Brand",
    label: "Growth & Brand",
    short: "Growth",
    color: "#8b5cf6",
    soft: "rgba(139,92,246,0.1)",
  },
  "Operations & Delivery": {
    key: "Operations & Delivery",
    label: "Operations & Delivery",
    short: "Ops",
    color: "#f59e0b",
    soft: "rgba(245,158,11,0.1)",
  },
  "Strategy & Culture": {
    key: "Strategy & Culture",
    label: "Strategy & Culture",
    short: "Strat",
    aliases: ["Strategy"],
    color: "#3b82f6",
    soft: "rgba(59,130,246,0.1)",
  },
  "Non Strategic": {
    key: "Non Strategic",
    label: "Non Strategic",
    short: "Admin",
    color: "#64748b",
    soft: "rgba(100,116,139,0.1)",
  },
  // Legacy alias — older sessions stored "Administrative & Internal" before the
  // dropdown options changed. Same color as Non Strategic so historical donuts
  // render consistently with the current Admin slot.
  "Administrative & Internal": {
    key: "Administrative & Internal",
    label: "Administrative & Internal",
    short: "Admin",
    color: "#64748b",
    soft: "rgba(100,116,139,0.1)",
  },
};

const DEFAULT_COLOR = "#94a3b8";
const DEFAULT_LABEL = "Uncategorized";

// Resolves both full names ("Revenue & Sales") AND short names ("Revenue") to the
// canonical PillarMeta. Short-name lookup is the back-compat path for callers that
// pass legacy strings (e.g. the trapDataCurrent / trapDataAfter rollups in page.tsx
// that hardcode "Strategy" | "Revenue" | "Growth" | "Ops" | "Admin").
export function normalizePillar(input: string | null | undefined): PillarMeta | null {
  if (!input) return null;
  if (PILLARS[input]) return PILLARS[input];
  for (const meta of Object.values(PILLARS)) {
    if (meta.short === input) return meta;
    if (meta.aliases && meta.aliases.includes(input)) return meta;
  }
  return null;
}

export function getPillarColor(input: string | null | undefined): string {
  return normalizePillar(input)?.color ?? DEFAULT_COLOR;
}

export function getPillarLabel(input: string | null | undefined): string {
  return normalizePillar(input)?.label ?? (input || DEFAULT_LABEL);
}

export function getPillarShort(input: string | null | undefined): string {
  return normalizePillar(input)?.short ?? (input || DEFAULT_LABEL);
}
